/**
 * Small building blocks shared by the compliance pages (ICANN, VNNIC, cases, locks):
 * translated enum badges, labelled selects, SLA/deadline cells, action dialogs and
 * form layout. Everything user-visible comes from the `compliance` namespace.
 */

import { Clock, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type Tone } from '@/components/Badge';
import { Field } from '@/components/blocks';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { compliance } from '@/i18n/locales/en/compliance';
import { formatDateTime, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';
import { isCode } from '@/lib/labels';
import { useCatalogue } from '../admin/ui';

export type EnumGroup = keyof typeof compliance.enums;
type ToneMap = Readonly<Record<string, { tone: Tone }>>;

/** Title-cases an enum value the translations do not know: `PENDING_REVIEW` -> `Pending review`. */
export function humanize(value: string): string {
  const s = value.replace(/[_.]+/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Translated label for a status/enum code; unknown codes fall back to the humanised code. */
export function useEnumLabel(): (group: EnumGroup, value: string | null | undefined) => string {
  const { t } = useTranslation('compliance');
  const lookup = t as unknown as (key: string, options: { defaultValue: string }) => string;
  return (group, value) => (!value ? '—' : isCode(value) ? lookup(`enums.${group}.${value}`, { defaultValue: humanize(value) }) : value);
}

/** Status badge: translated label, the raw code in `title`, tone from the vocabulary map. */
export function EnumBadge({ group, value, tones, tone, icon }: { group: EnumGroup; value: string | null | undefined; tones?: ToneMap; tone?: Tone; icon?: boolean }) {
  const label = useEnumLabel();
  if (!value) return <Badge tone="neutral">—</Badge>;
  return (
    <Badge tone={tone ?? tones?.[value]?.tone ?? tones?.[value.toUpperCase()]?.tone ?? 'neutral'} title={value} icon={icon}>
      {label(group, value)}
    </Badge>
  );
}

/** Options for a SelectField built from enum codes. */
export function useEnumOptions(): <T extends string>(group: EnumGroup, values: readonly T[]) => { value: T; label: string }[] {
  const label = useEnumLabel();
  return (group, values) => values.map((value) => ({ value, label: label(group, value) }));
}

const ALL = '__all__';

/** A labelled shadcn Select. `allLabel` adds an "any" option that maps to the empty string. */
export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  allLabel,
  disabled,
  className,
  hint,
}: {
  label: ReactNode;
  value: T | '';
  onChange: (value: T | '') => void;
  options: readonly { value: T; label: string }[];
  allLabel?: string;
  disabled?: boolean;
  className?: string;
  hint?: ReactNode;
}) {
  return (
    <Field label={label} className={cn('min-w-40', className)} hint={hint}>
      <Select value={value === '' ? ALL : value} onValueChange={(v) => onChange(v === ALL ? '' : (v as T))} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allLabel ? <SelectItem value={ALL}>{allLabel}</SelectItem> : null}
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** Stable sort that puts overdue rows first, keeping the server's deadline order otherwise. */
export function overdueFirst<T>(rows: readonly T[], isOverdue: (row: T) => boolean): T[] {
  return rows
    .map((row, i) => ({ row, i, o: isOverdue(row) ? 0 : 1 }))
    .sort((a, b) => a.o - b.o || a.i - b.i)
    .map((x) => x.row);
}

const DAY = 86_400_000;

/** The current time, refreshed every minute so deadline badges stay right on an open page. */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Deadline cell: overdue is a red badge plus the due time; a deadline within a day is
 * flagged "due soon"; no policy is a warning. Absolute times are UTC.
 */
export function DueCell({ dueAt, overdue, policyMissing, done }: { dueAt: string | null | undefined; overdue: boolean; policyMissing?: boolean; done?: ReactNode }) {
  const { t } = useTranslation('compliance');
  const now = useNow();
  if (done) return <span className="text-xs text-muted-foreground">{done}</span>;
  if (policyMissing || !dueAt) {
    return policyMissing ? (
      <Badge tone="warn" title={t('sla.noPolicyHint')}>
        {t('sla.noPolicy')}
      </Badge>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  const soon = !overdue && new Date(dueAt).getTime() - now < DAY;
  return (
    <div className="grid gap-0.5 whitespace-nowrap" title={formatDateTime(dueAt)}>
      <div className="flex items-center gap-1.5">
        {overdue ? (
          <Badge tone="crit">{t('sla.overdue')}</Badge>
        ) : soon ? (
          <Badge tone="warn">{t('sla.dueSoon')}</Badge>
        ) : (
          <Clock className="size-3.5 text-muted-foreground" aria-hidden="true" />
        )}
        <span className={cn('text-sm', overdue && 'font-medium text-critical')}>{formatRelative(dueAt)}</span>
      </div>
      <span className="text-xs text-muted-foreground tabular">{formatDateTime(dueAt)}</span>
    </div>
  );
}

/** Relative time with the absolute UTC time in the tooltip. */
export function When({ at }: { at: string | null | undefined }) {
  if (!at) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="whitespace-nowrap" title={formatDateTime(at)}>
      {formatRelative(at)}
    </span>
  );
}

/** Dialog shell for write forms. Clicking outside never discards a half-filled form. */
export function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', wide ? 'sm:max-w-2xl' : 'sm:max-w-lg')} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** "Needs <permission's name> and an MFA-verified session." */
export function MfaHint({ permission, className }: { permission?: string; className?: string }) {
  const { t } = useTranslation('compliance');
  const { permissionName } = useCatalogue();
  return (
    <p className={cn('flex items-start gap-2 text-xs text-muted-foreground', className)}>
      <ShieldCheck className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      <span>
        {permission ? (
          <>
            {t('common.needsPermission')} <span className="font-medium">“{permissionName(permission)}”</span> {t('common.andMfa')}
          </>
        ) : (
          t('common.needsMfa')
        )}
      </span>
    </p>
  );
}

/** Explicit confirmation checkbox for actions with external effect. */
export function ConfirmCheck({ checked, onChange, disabled, children }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children: ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
    <label className="flex items-start gap-2.5 rounded-md border border-warning/30 bg-warning-soft/60 p-3 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} disabled={disabled} className="mt-0.5" />
      <span>{children}</span>
    </label>
  );
}

export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid gap-4 sm:grid-cols-2', className)}>{children}</div>;
}

export function FormActions({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end', className)}>{children}</div>;
}

/** Field names as small code chips; approved ones are green when `approved` is given. */
export function FieldChips({ fields, approved }: { fields: string[]; approved?: string[] }) {
  const { t } = useTranslation('compliance');
  return (
    <span className="flex flex-wrap gap-1">
      {fields.map((f) => {
        const ok = approved?.includes(f);
        return (
          <code
            key={f}
            className={cn(
              'rounded border px-1.5 py-0.5 text-xs',
              approved ? (ok ? 'border-success/30 bg-success-soft text-success' : 'bg-muted text-muted-foreground line-through') : 'bg-muted',
            )}
            title={approved ? (ok ? t('common.approved') : t('common.notApproved')) : undefined}
          >
            {f}
          </code>
        );
      })}
    </span>
  );
}

/** Vertical timeline for case history. */
export function Timeline({
  items,
}: {
  items: {
    key: string;
    at: string | null;
    title: ReactNode;
    meta?: ReactNode;
    tone?: Tone;
  }[];
}) {
  const DOT: Record<Tone, string> = {
    neutral: 'bg-muted-foreground',
    info: 'bg-info',
    ok: 'bg-success',
    pending: 'bg-info',
    warn: 'bg-warning',
    serious: 'bg-serious',
    crit: 'bg-critical',
  };
  return (
    <ol className="relative ml-1.5 space-y-5 border-l pl-5">
      {items.map((it) => (
        <li key={it.key} className="relative">
          <span className={cn('absolute top-1.5 -left-[25px] size-2.5 rounded-full ring-4 ring-card', DOT[it.tone ?? 'info'])} aria-hidden="true" />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {it.at ? (
              <time dateTime={it.at} className="text-xs text-muted-foreground tabular">
                {formatDateTime(it.at)}
              </time>
            ) : null}
            {it.title}
          </div>
          {it.meta ? <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{it.meta}</div> : null}
        </li>
      ))}
    </ol>
  );
}

/** Success line after a write. */
export function SuccessNote({ children }: { children: ReactNode }) {
  return (
    <div role="status" className="rounded-lg border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
      {children}
    </div>
  );
}
